import type { UserSettingsType } from "@volgo/core/contract";
import { useState } from "react";
import { useSession } from "../session";
import { Button, LinkButton } from "../ui/Button";
import { describeError } from "../ui/errors";
import { ROLE_LABELS } from "../ui/labels";
import { Segmented } from "../ui/Segmented";
import { ErrorNote, Loading } from "../ui/States";
import { useLoad } from "../ui/useLoad";

const BLOCKED_CARDS = [
  { value: "show", label: "보이기" },
  { value: "hide", label: "숨기기" },
] as const;

const READER_DIRECTION = [
  { value: "normal", label: "왼쪽 이전 · 오른쪽 다음" },
  { value: "reverse", label: "왼쪽 다음 · 오른쪽 이전" },
] as const;

export function SettingsPage() {
  const { client, state, logout } = useSession();
  const settings = useLoad(() => client.call("users.getSettings"), "settings");
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);

  function save(body: Partial<UserSettingsType>) {
    setError(null);
    setSaving(true);
    client
      .call("users.patchSettings", { body })
      .then(settings.set, (e: unknown) => setError(e))
      .finally(() => setSaving(false));
  }

  return (
    <div className="page page-narrow">
      <div className="page-head">
        <h1>설정</h1>
        {/* phones have no sidebar: this is their way to the admin screens */}
        {state.user?.role === "admin" && <LinkButton to="/admin">관리</LinkButton>}
      </div>

      <section className="panel" aria-labelledby="settings-results">
        <h2 id="settings-results">카드 표시</h2>
        {settings.status === "loading" && <Loading />}
        {settings.status === "error" && <ErrorNote error={settings.error} onRetry={settings.reload} />}
        {settings.status === "ready" && (
          <fieldset disabled={saving}>
            <Segmented
              legend="차단 카드"
              options={BLOCKED_CARDS}
              isOn={(v) => v === (settings.data.hideBlockedCards ? "hide" : "show")}
              onPick={(v) => save({ hideBlockedCards: v === "hide" })}
            />
            <p className="note">
              차단된 태그가 붙은 갤러리는 표지 없이 제목만 있는 카드로 나옵니다. 숨기면 검색 결과에서
              빠집니다. 검색할 때마다 조건 메뉴의 “차단 갤러리”로 바꿀 수도 있습니다.
            </p>
          </fieldset>
        )}
        {error !== null && (
          <p className="note note-warn" role="alert">
            {describeError(error)}
          </p>
        )}
      </section>

      <section className="panel" aria-labelledby="settings-reader">
        <h2 id="settings-reader">리더</h2>
        {settings.status === "ready" && (
          <fieldset disabled={saving}>
            <Segmented
              legend="PC 넘김 방향"
              options={READER_DIRECTION}
              isOn={(v) => v === (settings.data.readerReverseNavigation ? "reverse" : "normal")}
              onPick={(v) => save({ readerReverseNavigation: v === "reverse" })}
            />
            <Segmented
              legend="모바일 넘김 방향"
              options={READER_DIRECTION}
              isOn={(v) => v === (settings.data.readerReverseNavigationMobile ? "reverse" : "normal")}
              onPick={(v) => save({ readerReverseNavigationMobile: v === "reverse" })}
            />
            <p className="note">한 쪽씩 볼 때 이미지의 왼쪽·오른쪽을 누르거나 방향키로 넘길 수 있습니다.</p>
          </fieldset>
        )}
      </section>

      <section className="panel" aria-labelledby="settings-account">
        <h2 id="settings-account">계정</h2>
        {state.user && (
          <dl className="facts">
            <div>
              <dt>이름</dt>
              <dd>{state.user.username}</dd>
            </div>
            <div>
              <dt>권한</dt>
              <dd>{ROLE_LABELS[state.user.role]}</dd>
            </div>
          </dl>
        )}
        <Button onClick={() => void logout()}>로그아웃</Button>
      </section>
    </div>
  );
}
